// src/pages/LitQuotesSection.jsx
//
// Student-facing render of a QUOTE BANK lit_section (sectionKind 'quotes'). Faithful port
// of the prototype LitQuoteBankView (between-the-lines-v2.html L13054-13111): a searchable,
// category-filterable, star-able list. Category set = LIT_QUOTE_CATEGORIES (L2522), inlined.
//
// Reads the single QUOTES_BLOCKS objectList (litSectionShape.js, mirrors LitQuoteBankEditor):
// blocks.quotes ([{act,speaker,category,quote,tags[]}]). Rows have no stable id, so the
// list index is the key/star key.
//
// Pure presentational (takes `content`). DEVIATION from the prototype: starring is
// session-local component state (useState), not persisted to localStorage/Firestore -- this
// is a read-only slice with no schema/storage writes. Search + filter behave identically.
import React, { useMemo, useState } from 'react';
import { IconSearch, IconStar } from '../components/icons.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

export default function LitQuotesSection({ content, done = false }) {
    const blocks = (content && content.blocks) || {};
    const quotes = Array.isArray(blocks.quotes) ? blocks.quotes : [];
    // Filter chips are DERIVED from the category values actually present ('All' + the
    // distinct categories, sorted). Danielle's quotes use thematic categories (e.g.
    // "Ambition", "Guilt & Conscience"), not a fixed Character/Theme/Symbol taxonomy,
    // so the old hardcoded chip set produced "No quotes match". Exact-match unchanged.
    const categories = useMemo(
        () => ['All', ...Array.from(new Set(quotes.map((qt) => qt.category).filter(Boolean))).sort()],
        [quotes],
    );
    const [category, setCategory] = useState('All');
    const [q, setQ] = useState('');
    const [onlyStarred, setOnlyStarred] = useState(false);
    const [starred, setStarred] = useState({});
    const toggleStar = (id) => setStarred((s) => ({ ...s, [id]: !s[id] }));
    const starredCount = Object.values(starred).filter(Boolean).length;

    const filtered = useMemo(() => {
        return quotes
            .map((it, i) => ({ it, i }))
            .filter(({ it, i }) => {
                if (onlyStarred && !starred[i]) return false;
                if (category !== 'All' && it.category !== category) return false;
                if (q.trim()) {
                    const needle = q.toLowerCase();
                    const inQuote = String(it.quote || '').toLowerCase().includes(needle);
                    const inSpeaker = String(it.speaker || '').toLowerCase().includes(needle);
                    const inTags = (Array.isArray(it.tags) ? it.tags : []).join(' ').toLowerCase().includes(needle);
                    if (!inQuote && !inSpeaker && !inTags) return false;
                }
                return true;
            });
    }, [quotes, category, q, onlyStarred, starred]);

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <LessonHeader eyebrow="Extras · Quote bank" title={(content && content.title) || 'Essential quotations'}
                blocks={blocks} done={done} />
            <p className="font-serif italic text-book-muted text-base -mt-4 mb-6">Every line in this list is a likely exam anchor. Star your favourites.</p>

            <div className="flex flex-wrap gap-3 mb-5">
                <div className="relative flex-grow min-w-[200px]">
                    <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-book-muted" />
                    {/* .selectable: a user's own search input is exempt from copy-block (ADR-009), like the AI drawer. */}
                    <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search quotes, speakers, tags..."
                        className="selectable w-full bg-book-pageDark border border-book-accent/30 pl-10 pr-3 py-2.5 text-book-text font-serif focus:outline-none focus:border-book-accent" />
                </div>
                <div className="inline-flex border border-book-accent/40 rounded-sm overflow-hidden">
                    {categories.map((c) => (
                        <button key={c} onClick={() => setCategory(c)}
                            className={`px-3 py-2 font-display text-sm transition-colors font-medium rounded-full ${category === c ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                            {c}
                        </button>
                    ))}
                </div>
                <label className="flex items-center gap-2 px-3 py-2 border border-book-accent/30 text-book-muted cursor-pointer">
                    <input type="checkbox" checked={onlyStarred} onChange={(e) => setOnlyStarred(e.target.checked)} />
                    <span className="font-serif italic text-sm">Starred only ({starredCount})</span>
                </label>
            </div>

            <div className="space-y-3">
                {filtered.length === 0 ? (
                    <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 text-center">
                        <p className="font-serif italic text-book-muted">{quotes.length === 0 ? 'Nothing here yet.' : 'No quotes match.'}</p>
                    </div>
                ) : filtered.map(({ it, i }) => (
                    <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 flex gap-4 items-start">
                        <button onClick={() => toggleStar(i)}
                            className={`p-1 flex-shrink-0 transition-colors ${starred[i] ? 'text-book-accent' : 'text-book-muted hover:text-book-accent'}`}>
                            <IconStar className="w-5 h-5" />
                        </button>
                        <div className="flex-grow min-w-0">
                            <p className="font-serif italic text-book-text text-base leading-relaxed mb-2">"{it.quote}"</p>
                            <div className="flex items-center gap-3 flex-wrap">
                                {it.speaker && <span className="font-display text-book-accent text-xs font-semibold">{it.speaker}</span>}
                                {it.act && <span className="font-display text-book-muted text-xs font-semibold">· {it.act}</span>}
                                {it.category && <span className="px-3 py-1.5 border border-book-accent/30 text-book-accent font-display text-sm font-medium rounded-full">{it.category}</span>}
                                {(Array.isArray(it.tags) ? it.tags : []).map((t, j) => (<span key={j} className="font-serif italic text-book-muted text-xs">· {t}</span>))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
